import { useMemo } from 'react'
import { TaggableUser } from 'types'

import { useMentionableUsers } from './useMentionableUsers'

export interface UseUserSearchOptions {
  /**
   * Minimum number of characters required before searching
   */
  minSearchLength?: number
  /**
   * Maximum number of results to return
   */
  maxResults?: number
}

export interface UseUserSearchResult {
  /**
   * All available users
   */
  allUsers: TaggableUser[]
  /**
   * Whether user data is loading
   */
  isLoading: boolean
  /**
   * Error message if user loading failed
   */
  error: string | null
  /**
   * Search function to filter users by query
   */
  searchUsers: (query: string) => TaggableUser[]
}

/**
 * Hook for searching and filtering users for mentions
 */
export const useUserSearch = (
  options: UseUserSearchOptions = {}
): UseUserSearchResult => {
  const { minSearchLength = 1, maxResults = 10 } = options

  const { data: users = [], error, isLoading } = useMentionableUsers()

  const searchUsers = useMemo(() => {
    return (query: string): TaggableUser[] => {
      if (!query || query.length < minSearchLength) {
        return []
      }

      const normalizedQuery = query.toLowerCase().trim()

      if (!users || users.length === 0) {
        return []
      }

      // Filter users based on name and email
      const filteredUsers = users
        .filter((user) => {
          const name = user.name?.toLowerCase() || ''
          const email = user.email?.toLowerCase() || ''

          return (
            name.includes(normalizedQuery) ||
            email.includes(normalizedQuery) ||
            name.startsWith(normalizedQuery) ||
            email.startsWith(normalizedQuery)
          )
        })
        .sort((a, b) => {
          // Sort by exact match first, then alphabetically
          const aName = a.name?.toLowerCase() || ''
          const bName = b.name?.toLowerCase() || ''

          const aExactMatch = aName.startsWith(normalizedQuery)
          const bExactMatch = bName.startsWith(normalizedQuery)

          if (aExactMatch && !bExactMatch) return -1
          if (!aExactMatch && bExactMatch) return 1

          return aName.localeCompare(bName)
        })
        .slice(0, maxResults)

      return filteredUsers
    }
  }, [users, minSearchLength, maxResults])

  return {
    allUsers: users || [],
    isLoading,
    error: error || null,
    searchUsers,
  }
}
